import DescribedLabel from '~/src/components/utils/form/DecribedLabel'
import FormikTextField from '~/src/components/mui-wrappers/fields/FormikTextField'
import { FormikProps } from 'formik'
import { useEoValue } from '~/src/hooks/useEoState'
import { Country, Firm } from '@eo-storefronts/eo-core'
import { FIRM_SELECTOR } from '~/src/stores/firm'
import { useTranslations } from '~/src/hooks/useTranslations'
import { AddressFormInterface } from '~/src/types/forms/AddressFormInterface'
import FormikSelectField from '~/src/components/mui-wrappers/fields/FormikSelectField'
import { LANGUAGE_STATE } from '~/src/stores/lang'
import FormikAddressAutocomplete from '~/src/components/mui-wrappers/fields/address'

interface Props {
  form: FormikProps<AddressFormInterface>,
}

const AccountAddressFields = ({ form }: Props) => {
  const lang = useEoValue(LANGUAGE_STATE)
  const firm = useEoValue<Firm | null>(FIRM_SELECTOR)
  const { t } = useTranslations()

  const _getGeocoderAddressComponentElement = (
    address: google.maps.GeocoderResult,
    type: string
  ): google.maps.GeocoderAddressComponent | undefined => {
    return address.address_components.filter((component: google.maps.GeocoderAddressComponent) => {
      return component.types.includes(type)
    })[0]
  }

  const _handleOnAddressSelected = async (address: google.maps.GeocoderResult | null): Promise<void> => {
    if (!address) {
      return
    }

    const streetNumber: google.maps.GeocoderAddressComponent | undefined = _getGeocoderAddressComponentElement(address, 'street_number')
    const street: google.maps.GeocoderAddressComponent | undefined = _getGeocoderAddressComponentElement(address, 'route')
    const subLocality: google.maps.GeocoderAddressComponent | undefined = _getGeocoderAddressComponentElement(address, 'sublocality')
    const locality: google.maps.GeocoderAddressComponent | undefined = _getGeocoderAddressComponentElement(address, 'locality')
    const postalCode: google.maps.GeocoderAddressComponent | undefined = _getGeocoderAddressComponentElement(address, 'postal_code')
    const country: google.maps.GeocoderAddressComponent | undefined = _getGeocoderAddressComponentElement(address, 'country')

    await form.setFieldValue('street', `${street?.long_name} ${streetNumber?.long_name ?? ''}`)
    await form.setFieldValue('zipCode', postalCode?.long_name)
    await form.setFieldValue('locality', subLocality?.long_name || locality?.long_name)

    if (country?.short_name) {
      await form.setFieldValue('country', country.short_name)
    }
  }

  return (
    <>
      <DescribedLabel
        gridArea='name'
        label={t('profile.address.addressName')}
        description={t('profile.address.addressNameDescription')}
      />
      <FormikTextField
        gridArea='name-field'
        TextFieldProps={{
          form,
          color: 'content',
          name: 'name',
          label: t('profile.address.description'),
          size: 'small'
        }}
      />
      <DescribedLabel
        gridArea='address'
        label={t('profile.address.address')}
        description={t('profile.address.addressDescription')}
      />
      <FormikAddressAutocomplete
        gridArea='street-field'
        TextFieldProps={{
          form,
          name: 'street',
          color: 'content',
          label: t('profile.address.streetAndHouseNumber'),
          autoComplete: 'new-street',
          helperText: t('global.required')
        }}
        onAddressSelected={_handleOnAddressSelected}
      />
      {/*<FormikTextField
        gridArea='houseNumber-field'
        TextFieldProps={{
          form,
          fullWidth: false,
          color: 'content',
          name: 'houseNumber',
          label: t('profile.address.houseNumber'),
          size: 'small'
        }}
      />*/}
      <DescribedLabel
        gridArea='city'
        label={t('profile.address.locality')}
        description={t('profile.address.localityDescription')}
      />
      <FormikTextField
        gridArea='locality-field'
        TextFieldProps={{
          form,
          name: 'locality',
          color: 'content',
          label: t('profile.address.locality'),
          size: 'small'
        }}
      />
      <FormikTextField
        gridArea='zipCode-field'
        TextFieldProps={{
          form,
          color: 'content',
          name: 'zipCode',
          label: t('profile.address.zipCode'),
          size: 'small'
        }}
      />
      <DescribedLabel
        gridArea='country'
        label={t('profile.address.country')}
        description={t('profile.address.countryDescription')}
      />
      <FormikSelectField
        gridArea='country-field'
        SelectFieldProps={{
          form: form,
          color: 'content',
          name: 'country',
          label: t('profile.address.country'),
          size: 'small'
        }}
      >
        {firm?.countries?.map((country: Country) => (
          <option
            key={country.id}
            value={country.iso}
          >
            {country.name[lang]}
          </option>
        ))}
      </FormikSelectField>
    </>
  )
}

export default AccountAddressFields
